import { resolveTagBadgeColourSwatch, type TagBadgeColour } from '@/shared/data/tagBadgeColours';
import { isLightTheme } from '@/theme/semanticTokens';
import { Chip } from '@/ui/primitives';
import { resolveLightTagBadgeStyle } from './tagBadgePresentation';
import { tagChipSx } from './tagChipStyles';
import type { ChipProps, SxProps, Theme } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type TagChipProps = {
  className?: string;
  label: ReactNode;
  tone: ChipProps['tone'];
  sx?: SxProps<Theme>;
};

const resolveTagChipBadgeColour = (tone: ChipProps['tone']): TagBadgeColour => {
  if (tone === 'green' || tone === 'success') return 'green';
  if (tone === 'red' || tone === 'error') return 'red';
  if (tone === 'orange' || tone === 'yellow' || tone === 'warning') {
    return 'orange';
  }
  if (tone === 'blue' || tone === 'info') return 'blue';
  if (tone === 'purple') return 'purple';
  if (tone === 'teal' || tone === 'primary') return 'teal';
  if (tone === 'brown') return 'brown';
  return 'neutral';
};

const resolveTagChipBackgroundColor = (tone: ChipProps['tone']): string =>
  resolveTagBadgeColourSwatch(resolveTagChipBadgeColour(tone));

const buildTagChipAccentSx = (tone: ChipProps['tone']): SxProps<Theme> => {
  const backgroundColor = resolveTagChipBackgroundColor(tone);
  const lightStyle = resolveLightTagBadgeStyle(resolveTagChipBadgeColour(tone));
  const darkBackgroundColor = backgroundColor;

  return {
    backgroundColor: (theme) =>
      isLightTheme(theme) ? lightStyle.backgroundColor : darkBackgroundColor,
    border: (theme) =>
      isLightTheme(theme) ? `1px solid ${lightStyle.borderColor}` : '1px solid transparent',
    backgroundImage: 'none',
    color: (theme) => (isLightTheme(theme) ? lightStyle.color : '#ffffff'),
    opacity: 1,
    '& .MuiChip-label': {
      color: 'inherit',
    },
    '& .MuiChip-label .MuiTypography-root': {
      color: 'inherit',
    },
    '&:hover, &.Mui-focusVisible, &:active': {
      backgroundColor: (theme) =>
        isLightTheme(theme) ? lightStyle.backgroundColor : darkBackgroundColor,
      opacity: 1,
    },
  };
};

export const TagChip = ({ className, label, tone, sx }: TagChipProps): ReactElement => {
  const accentSx = buildTagChipAccentSx(tone);
  const resolvedSx = sx
    ? ([tagChipSx, accentSx, sx] as SxProps<Theme>)
    : ([tagChipSx, accentSx] as SxProps<Theme>);

  return <Chip className={className} size="xs" tone={tone} label={label} sx={resolvedSx} />;
};
